import { createFileRoute } from "@tanstack/react-router";
import {
	ArticleCardLink,
	ContentEmptyState,
	ContentErrorState,
	ContentHero,
} from "@/components/blocks/editorial";
import { Skeleton } from "@/components/ui/skeleton";
import { getCanonicalPageUrl } from "@/lib/ai-content";
import { content } from "@/lib/content";
import { getTranslator } from "@/lib/i18n";
import { resolveLocaleForPath } from "@/lib/i18n/locale";
import { useI18n } from "@/lib/i18n/use-i18n";
import { buildItemListStructuredData, buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/{-$locale}/blog/")({
	component: BlogListPage,
	loader: async ({ location, serverContext }) => {
		const locale = resolveLocaleForPath(location.pathname, serverContext);
		const posts = await content.getPosts({ data: { locale } });
		return { posts, locale };
	},
	head: ({ loaderData }) => {
		if (!loaderData) return {};
		const t = getTranslator(loaderData.locale);
		return buildSeo({
			title: t((t) => t.seo.blogTitle),
			description: t((t) => t.blog.description),
			path: "/blog",
			locale: loaderData.locale,
			structuredData: buildItemListStructuredData(
				loaderData.posts.map((post) => ({
					name: post.title,
					url: getCanonicalPageUrl(`/blog/${post.slug}`, loaderData.locale),
				})),
			),
		});
	},
	pendingComponent: () => (
		<div className="mx-auto max-w-6xl px-6 py-20">
			<div className="mb-16 space-y-4">
				<Skeleton className="mx-auto h-10 w-32" />
				<Skeleton className="mx-auto h-5 w-80" />
			</div>
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
				{[...Array(6)].map((_, i) => (
					<Skeleton key={i} className="aspect-[4/3] w-full rounded-xl" />
				))}
			</div>
		</div>
	),
	errorComponent: ({ error }) => <BlogError error={error} />,
});

function BlogListPage() {
	const { posts } = Route.useLoaderData();
	const { t, localeParam } = useI18n();
	const localeParams: Record<string, string> = localeParam
		? { locale: localeParam }
		: {};
	const [featuredArticle, ...otherArticles] = posts;

	return (
		<>
			<ContentHero
				label={t((t) => t.blog.heroLabel)}
				title={t((t) => t.blog.heroTitle)}
				description={t((t) => t.blog.description)}
			/>

			<section className="mx-auto w-full max-w-6xl px-6 pt-12 pb-20">
				{posts.length === 0 ? (
					<ContentEmptyState>{t((t) => t.blog.empty)}</ContentEmptyState>
				) : (
					<>
						{featuredArticle ? (
							<div className="mb-16">
								<ArticleCardLink
									article={featuredArticle}
									localeParams={localeParams}
									placement="blog_featured"
									variant="featured"
									featuredLabel={t((t) => t.blog.featured)}
								/>
							</div>
						) : null}

						{otherArticles.length > 0 ? (
							<div>
								<h2 className="mb-8 text-2xl font-semibold tracking-tight">
									{t((t) => t.blog.latest)}
								</h2>
								<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
									{otherArticles.map((article) => (
										<ArticleCardLink
											key={article.slug}
											article={article}
											localeParams={localeParams}
											placement="blog_list"
											variant="grid"
										/>
									))}
								</div>
							</div>
						) : null}
					</>
				)}
			</section>
		</>
	);
}

function BlogError({ error }: { error: Error }) {
	const { t } = useI18n();

	return (
		<ContentErrorState
			error={error}
			title={t((t) => t.blog.errorTitleList)}
			fallback={t((t) => t.blog.errorFallback)}
			source="blog_list"
		/>
	);
}
